import React, { useState } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Box, 
  Button, 
  Menu, 
  MenuItem, 
  IconButton, 
  Tooltip
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import LanguageSwitcher from './LanguageSwitcher';
import NotificationBell from './NotificationBell';
import { 
  AccountCircle, 
  Brightness4, 
  Brightness7,
} from '@mui/icons-material';

const Navbar: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const { isDarkMode, toggleTheme } = useCustomTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Функция за определяне на действителния тип на потребителя
  const getUserType = (): string => {
    // Първо проверяваме от контекста (който идва от API)
    if (user?.user_type) {
      return user.user_type;
    }
    
    // Ако липсва, проверяваме в localStorage (който запазихме допълнително)
    const storedType = localStorage.getItem('user_type');
    if (storedType) {
      return storedType;
    }
    
    // Проверяваме имейла на потребителя (специален случай за професионалист)
    if (user?.email === 'mitat1976@abv.bg') {
      return 'professional';
    }
    
    // По подразбиране връщаме 'client'
    return 'client';
  };

  const handleProfile = () => {
    handleClose();
    
    const userType = getUserType();
    
    if (userType === 'professional') {
      navigate(`/profile/professional/${user?.id}`);
    } else {
      navigate(`/profile/client/${user?.id}`);
    }
  };

  const handleLogout = () => {
    handleClose();
    logout();
    navigate('/');
  };

  return (
    <AppBar position="static">
      <Toolbar>
        <Box
          component={RouterLink}
          to="/"
          sx={{
            flexGrow: 1,
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
          }}
          aria-label="MasterBook home"
        >
          <Box
            component="img"
            src="/brand/logo-full.svg"
            alt="MasterBook"
            sx={{
              height: { xs: 30, sm: 38 },
              width: 'auto',
              display: 'block',
              maxWidth: { xs: 140, sm: 220 },
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1.5 }, alignItems: 'center', flexShrink: 0 }}>
          <LanguageSwitcher />
          
          {/* Бутон за превключване на темата */}
          <Tooltip title={isDarkMode ? t('theme.light_mode') : t('theme.dark_mode')}>
            <IconButton 
              color="inherit" 
              onClick={toggleTheme}
              sx={{ ml: 1 }}
            >
              {isDarkMode ? <Brightness7 /> : <Brightness4 />}
            </IconButton>
          </Tooltip>
          
          {isAuthenticated ? (
            <>
              <Button
                component={RouterLink}
                to={getUserType() === 'professional' 
                  ? `/profile/professional/${user?.id}/dashboard` 
                  : `/profile/client/${user?.id}/dashboard`}
                sx={{ color: 'white', display: { xs: 'none', md: 'inline-flex' } }}
              >
                {t('common.dashboard')}
              </Button>
              {user?.is_superuser && (
                <Button
                  component={RouterLink}
                  to="/admin"
                  sx={{ color: 'white', display: { xs: 'none', md: 'inline-flex' } }}
                >
                  Admin
                </Button>
              )}

              <NotificationBell />
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton
                  size="large"
                  aria-label="account of current user"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleMenu}
                  color="inherit"
                >
                  <AccountCircle />
                </IconButton>
                <Menu
                  id="menu-appbar"
                  anchorEl={anchorEl}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                >
                  <MenuItem onClick={() => {
                    handleClose();
                    const userType = getUserType();
                    const userId = user?.id?.toString();
                    if (!userId) return;
                    navigate(userType === 'professional'
                      ? `/profile/professional/${userId}/dashboard`
                      : `/profile/client/${userId}/dashboard`);
                  }}>
                    {t('common.dashboard')}
                  </MenuItem>
                  {user?.is_superuser && (
                    <MenuItem
                      onClick={() => {
                        handleClose();
                        navigate('/admin');
                      }}
                    >
                      Admin
                    </MenuItem>
                  )}
                  <MenuItem onClick={handleProfile}>
                    {t('common.my_profile')}
                  </MenuItem>
                  <MenuItem onClick={() => { 
                    handleClose(); 
                    
                    if (!user || !user.id) {
                      console.error('User or user ID is undefined');
                      return;
                    }
                    
                    const userType = getUserType();
                    const userId = user.id.toString();

                    if (userType === 'professional') {
                      navigate(`/profile/professional/${userId}/messages`);
                    } else {
                      navigate(`/profile/client/${userId}/messages`);
                    }
                  }}>
                    {t('chat.title')}
                  </MenuItem>
                  {getUserType() === 'professional' && (
                    <MenuItem
                      onClick={() => {
                        handleClose();
                        const userId = user?.id?.toString();
                        if (!userId) return;
                        navigate(`/profile/professional/${userId}/agenda`);
                      }}
                    >
                      {t('agenda.title')}
                    </MenuItem>
                  )}
                  <MenuItem onClick={handleLogout}>
                    {t('common.logout')}
                  </MenuItem>
                </Menu>
              </Box>
            </>
          ) : (
            <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.5 }, alignItems: 'center' }}>
              <RouterLink to="/login" style={{ color: 'white', textDecoration: 'none', fontSize: '0.9rem' }}>
                {t('common.login')}
              </RouterLink>
              <RouterLink to="/register" style={{ color: 'white', textDecoration: 'none', fontSize: '0.9rem' }}>
                {t('common.register')}
              </RouterLink>
            </Box>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar; 